import {useCallback, useEffect, useMemo, useState} from "react";
import type {MultiValue} from "react-select";

import {memoWithType} from "../../react";
import ReactNativeInputSelect from "../ReactNativeInputSelect";
import type {OptionType} from "../Select";
import {Select} from "../Select";

import type {SelectGenericProps} from "./types";

// We can't simply infer by `ComponentProps` because it contains generic type
export type Props<T extends string | number | boolean> = SelectGenericProps<T> & {
    testID?: string;
    isDisabled?: boolean;
    placeholder?: string;
    isOpen?: boolean;
    isLoading?: boolean;
    maxMenuHeight?: number;
    isClearable?: boolean;
    isSearchable?: boolean;
    className?: string;
};

function SelectGeneric<T extends string | number | boolean>({
    testID,
    value,
    isMulti,
    options,
    isDisabled = false,
    placeholder = "Select an option",
    isOpen,
    maxMenuHeight,
    isClearable,
    isSearchable = true,
    isLoading,
    className,
    onChange
}: Props<T>) {
    // `null` is to clear the value on select on the web
    const [selectedOption, setSelectedOption] = useState<OptionType<T> | OptionType<T>[] | null>(
        null
    );

    const flatOptions = useMemo(() => {
        return options.flatMap(option => {
            return "options" in option ? option.options : [option];
        });
    }, [options]);

    useEffect(() => {
        setSelectedOption(prev => {
            if (isMulti) {
                return flatOptions.filter(option => value?.includes(option.value));
            }

            if (Array.isArray(prev)) {
                return prev;
            }

            if (!prev || !prev.value || prev.value !== value) {
                return typeof value !== "undefined"
                    ? flatOptions.find(option => option.value === value) ?? null
                    : null;
            }
            return prev;
        });
    }, [flatOptions, isMulti, value]);

    const handleChange = useCallback(
        (value: OptionType<T> | null) => {
            if (isMulti) {
                return;
            }
            setSelectedOption(value ?? null);
            if (value) {
                onChange?.(value.value, value);
            }
        },
        [isMulti, onChange]
    );

    const handleChangeMultiValue = useCallback(
        (value: MultiValue<OptionType<T>>) => {
            const values = [...value];
            setSelectedOption(values);
            if (values && isMulti) {
                onChange?.(values.map(v => v.value));
            }
        },
        [isMulti, onChange]
    );

    const handleChangeReactNativeInputSelect = useCallback(
        (value: T) => {
            if (isMulti) {
                return;
            }
            const option = flatOptions.find(option => option.value === value);
            setSelectedOption(option ?? null);
            if (option) {
                onChange?.(option.value, option);
            }
        },
        [flatOptions, isMulti, onChange]
    );

    const handleChangeReactNativeInputSelectMulti = useCallback(
        (value: T[]) => {
            if (!isMulti) {
                return;
            }
            const options = flatOptions.filter(option => value.includes(option.value));
            setSelectedOption(options);
            onChange?.(options.map(v => v.value));
        },
        [flatOptions, isMulti, onChange]
    );

    if (process.env.IS_WEB) {
        if (isMulti) {
            return (
                <Select
                    isMulti
                    className={className}
                    isClearable={isClearable}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    isSearchable={isSearchable}
                    maxMenuHeight={maxMenuHeight}
                    menuIsOpen={isOpen}
                    options={options}
                    placeholder={placeholder}
                    testID={testID}
                    value={selectedOption}
                    onChange={handleChangeMultiValue}
                />
            );
        }
        return (
            <Select
                className={className}
                isClearable={isClearable}
                isDisabled={isDisabled}
                isLoading={isLoading}
                isSearchable={isSearchable}
                maxMenuHeight={maxMenuHeight}
                menuIsOpen={isOpen}
                options={options}
                placeholder={placeholder}
                testID={testID}
                value={selectedOption}
                onChange={handleChange}
            />
        );
    }

    // for the app

    if (isMulti) {
        return (
            <ReactNativeInputSelect
                isMulti={isMulti}
                isSearchable={isSearchable}
                options={options}
                placeholder={placeholder}
                testID={testID ?? "select-generic"}
                value={value}
                onChange={handleChangeReactNativeInputSelectMulti}
            />
        );
    }

    return (
        <ReactNativeInputSelect
            className={className}
            isSearchable={isSearchable}
            options={options}
            placeholder={placeholder}
            testID={testID ?? "select-generic"}
            value={value}
            onChange={handleChangeReactNativeInputSelect}
        />
    );
}

export default memoWithType(SelectGeneric);
